import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CanActivateChild, Router } from '@angular/router';
import { ClaimsDataService } from 'app/commercial/claims/services/claims-data.service';
import { PersonalConstants } from 'app/common/personalConstants';
import { ClaimsOidcAuthService } from 'app/efnol-modernization/claims-oidc-auth/claims-oidc-auth.service';
import { IamBypass } from 'app/shared/services/iam-bypass.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { Observable } from 'rxjs';

@Injectable()
export class CommercialClaimsGuard implements CanActivateChild {
    private readonly commercialDestinationKey = 'commercialDestination';
    private readonly commercialRoutes = [
        {
            path: '/commercial/claims/policy-claims-list',
            destination: 'policy-claims-list'
        },
        {
            path: '/commercial/claims/claim-overview',
            destination: 'claim-overview'
        },
        {
            path: '/commercial/claims/pak-claims-list',
            destination: 'pak-claims-list'
        },
        {
            path: '/commercial/claims/farm-flex-deductible',
            destination: 'farm-flex-deductible'
        }
    ];

    constructor (
        private sessionService: SessionService,
        private authService: ClaimsOidcAuthService,
        private claimsDataService: ClaimsDataService,
        private iamByPass: IamBypass,
        private utils: UtilService,
        private router: Router,
        @Inject(DOCUMENT) private document: Document
    ) {}

    canActivate(): Observable<boolean> | boolean {
        const urlFragment = this.getCurrentURL();
        const queryParamsFound = this.utils.getQueryParams(urlFragment) || {};
        this.hydrateBypassSession(queryParamsFound);

        const isExternalRedirect = this.isCommercialExternalRedirect(queryParamsFound, urlFragment);
        const isMontyBotRedirect = this.isViaMontyBot(queryParamsFound);
        const isViaMontyFlagSet = this.sessionService.getSessionItemDecrypted(PersonalConstants.LOGIN_ENTRY_POINTS.IAMBYPASS) === 'true' ||
            this.iamByPass.userArrivedViaBypass();
        const isInternalRouting = this.isInternalRouting(isExternalRedirect, isViaMontyFlagSet);
        const isCommercialRoute = this.isCommercialClaimsRoute(urlFragment);
        this.setCommercialDestination(queryParamsFound, urlFragment);

        if (isExternalRedirect && !isMontyBotRedirect) {
            return this.startExternalRouteSessionCheck();
        }

        if ((!isExternalRedirect && isMontyBotRedirect) || isInternalRouting) {
            return true;
        }

        if (!isExternalRedirect && !isInternalRouting && isCommercialRoute) {
            return this.startExternalRouteSessionCheck();
        }

        this.router.navigate([PersonalConstants.URLS.error500]);
        return false;
    }

    canActivateChild(): Observable<boolean> | boolean {
        return this.canActivate();
    }

    getCurrentURL(): string {
        return this.document.location.href;
    }

    isCommercialExternalRedirect(queryParamsFound: any, url: string): boolean {
        const montyBot = this.isViaMontyBot(queryParamsFound);
        const isCommercialRoute = this.isCommercialClaimsRoute(url);

        if (queryParamsFound?.pkey && !montyBot && isCommercialRoute) {
            this.clearPriorPolicyInfo();
            this.sessionService.setSessionItem(PersonalConstants.FILE_CLAIM_COMPONENT.POLICYNUMBER, this.utils.formatPolicyNumber(queryParamsFound.pkey));
            window.sessionStorage.setItem('loginState', PersonalConstants.LOGIN_ENTRY_POINTS.COMMERCIAL_CLAIMS_LIST);
        }

        return isCommercialRoute && !!queryParamsFound?.pkey && !montyBot;
    }

    clearPriorPolicyInfo(): void {
        this.claimsDataService.clearCommercialClaimsSession();
        sessionStorage.removeItem('policyNumber');
        sessionStorage.removeItem('nw-policyNumber');
        sessionStorage.removeItem('policy-type');
        sessionStorage.removeItem('Claim-Number-Stored-In-Session');
    }

    isInternalRouting(isExternalRedirect: boolean, isViaMontyFlagSet: boolean): boolean {
        let isUserRegAuthorized = false;
        this.authService.getAuthStatus().subscribe((status) => {
            isUserRegAuthorized = status.isAuthorized && status.isRegistered;
        });

        return isUserRegAuthorized && (!isExternalRedirect || isViaMontyFlagSet);
    }

    isViaMontyBot(queryParamsFound?: any): boolean {
        return queryParamsFound?.IAM_BYPASS === 'true' ||
            sessionStorage.getItem(PersonalConstants.LOGIN_ENTRY_POINTS.ARRIVED_FROM_TEST_PAGE) === 'true' ||
            this.sessionService.getSessionItemDecrypted(PersonalConstants.LOGIN_ENTRY_POINTS.IAMBYPASS) === 'true';
    }

    isCommercialClaimsRoute(url: string): boolean {
        return this.commercialRoutes.some((route) => url.includes(route.path));
    }

    private setCommercialDestination(queryParamsFound: any, url: string): void {
        if (!this.isCommercialClaimsRoute(url)) {
            return;
        }

        const destination = queryParamsFound?.destinationPageName || this.getDestinationFromRoute(url);
        if (destination) {
            sessionStorage.setItem(this.commercialDestinationKey, destination);
        } else {
            sessionStorage.removeItem(this.commercialDestinationKey);
        }
    }

    private getDestinationFromRoute(url: string): string {
        const matchedRoute = this.commercialRoutes.find((route) => url.includes(route.path));
        return matchedRoute ? matchedRoute.destination : '';
    }

    private hydrateBypassSession(queryParamsFound: any): void {
        if (queryParamsFound?.IAM_BYPASS === 'true') {
            sessionStorage.setItem(PersonalConstants.LOGIN_ENTRY_POINTS.ARRIVED_FROM_TEST_PAGE, 'true');
            this.sessionService.setSessionItem(PersonalConstants.LOGIN_ENTRY_POINTS.IAMBYPASS, 'true');
            window.sessionStorage.setItem('loginState', PersonalConstants.LOGIN_ENTRY_POINTS.COMMERCIAL_CLAIMS_LIST);

            if (queryParamsFound?.pkey) {
                const formattedPolicyNumber = this.utils.formatPolicyNumber(queryParamsFound.pkey);

                this.sessionService.setSessionItem(PersonalConstants.FILE_CLAIM_COMPONENT.POLICYNUMBER, formattedPolicyNumber);
                sessionStorage.setItem('policyNumber', formattedPolicyNumber);
                sessionStorage.setItem('nw-policyNumber', formattedPolicyNumber);
                sessionStorage.setItem('claimsSelectedPolicyNumber', formattedPolicyNumber);

                try {
                    const claimNavigationData = JSON.parse(sessionStorage.getItem('claimNavigationData') || '{}');
                    claimNavigationData.claimsSelectedPolicyNumber = formattedPolicyNumber;
                    sessionStorage.setItem('claimNavigationData', JSON.stringify(claimNavigationData));
                } catch (_error) {
                    sessionStorage.setItem(
                        'claimNavigationData',
                        JSON.stringify({ claimsSelectedPolicyNumber: formattedPolicyNumber })
                    );
                }
            }
        }
    }

    private startExternalRouteSessionCheck(): Observable<boolean> | boolean {
        sessionStorage.removeItem('arrivedViaIAMBypass');
        return this.authService.checkActiveSessionOnExternalRoute(PersonalConstants.LOGIN_ENTRY_POINTS.COMMERCIAL_CLAIMS_LIST);
    }
}
